import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { Button, Stack, Typography } from '@mui/material';

import { componentsSize } from 'constants/components';
import { formatDate } from 'helper/DateLib';
import { DatePicker, Input } from 'components/simple';

const formPrefix = 'key-event-form';
const styles = {
  inputWrapper: {
    flex: 1,
  },
  datePicker: {
    wrapper: {
      maxWidth: 'unset',
    },
  },
  nameInput: ({ palette }) => ({
    flex: 1,
    '.MuiInputBase-root': {
      borderRadius: '10px !important',
      height: 56,
      '.MuiInputBase-input::placeholder': {
        color: palette.colors.lightGrey,
      },
    },
  }),
  nameLabel: {
    fontWeight: 500,
  },
};

export const KeyEventForm = ({ formId = null, keyEventsLimits, defaultValues, onSubmit, onCancel }) => {
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = useForm({ defaultValues, mode: 'all' });

  return (
    <form onSubmit={handleSubmit(onSubmit)} id={formId}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={{ xs: 3, lg: 8 }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Event name is required' }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                id={`${formPrefix}-name`}
                error={error}
                innerRef={ref}
                placeholder="Key event name"
                customStyles={{ control: styles.nameInput, label: styles.nameLabel }}
                inputProps={{ maxLength: 120 }}
                label="Key Event name"
              />
            )}
          />

          <Controller
            name="date"
            control={control}
            rules={{ required: 'Event date is required' }}
            render={({ field, fieldState: { error } }) => (
              <Stack spacing={1} sx={styles.inputWrapper}>
                <Typography variant="subtitle1" component="p">
                  Key Event date
                </Typography>

                <DatePicker
                  {...field}
                  placeholder={formatDate()}
                  id={`${formPrefix}-date-input`}
                  error={!!error}
                  errorText={error?.message}
                  customStyles={styles.datePicker}
                  shouldDisableDate={(date) =>
                    !keyEventsLimits.some(({ start, end }) => !date.isBefore(start, 'day') && !date.isAfter(end, 'day'))
                  }
                  popperPlacement="left"
                />
              </Stack>
            )}
          />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button size={componentsSize.small} color="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button type="submit" size={componentsSize.small} disabled={!isValid} loading={isSubmitting}>
            Save
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

KeyEventForm.propTypes = {
  formId: PropTypes.string,
  keyEventsLimits: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  ).isRequired,
  defaultValues: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    date: PropTypes.instanceOf(Date),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
